import { DataLockedAndLoadedCard, ICON_SIZES, ReviewCard } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { MARKETING_SITE } from '@backrs_ui/grow-ui/src/settings';

import { Box, Theme, SxProps, useMediaQuery, keyframes } from '@mui/material';

import { OnboardingSidebarItems } from './OnboardingSidebarItems';
import { VisibleStep } from './utils/getVisibleSteps';
import { StepKey } from './utils/stepNavigation';

const BACKRS_MARKETING_SITE = MARKETING_SITE;
const DATA_LOCKED_STRINGS = appStrings.modules.onboarding.dataLockedCards;
const REVIEW_CARD_STRINGS = appStrings.modules.onboarding.reviewCards;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export type SidebarCard = 'data-locked' | 'matteo' | 'aurella' | 'sayvon' | 'nesha' | undefined;

type OnboardingSidebarProps = Readonly<{
  currentStep: StepKey;
  isShowingAccountCreationLoading: boolean;
  isShowingInviteFamily: boolean;
  sidebarCard?: SidebarCard;
  showInviteFamily: boolean;
  visibleSteps: VisibleStep[];
  currentProgressStepIndex: number;
  canNavigateBack: boolean;
  onProgressStepClick: (stepIndex: number) => void;
}>;

const dataLockedConfig = {
  'data-locked': {
    imageUrl: '/images/data-hexagon.svg',
    backrsPage: 'data-pledge',
    ...DATA_LOCKED_STRINGS['data-locked'],
  },
};

const reviewCardConfig = {
  matteo: {
    profileImage: '/profile-pics/matteo.png',
    shrinkText: false,
    ...REVIEW_CARD_STRINGS.matteo,
  },
  aurella: {
    profileImage: '/profile-pics/aurella.png',
    shrinkText: true,
    ...REVIEW_CARD_STRINGS.aurella,
  },
  sayvon: {
    profileImage: '/profile-pics/sayvon.png',
    shrinkText: true,
    ...REVIEW_CARD_STRINGS.sayvon,
  },
  nesha: {
    profileImage: '/profile-pics/nesha.png',
    shrinkText: true,
    ...REVIEW_CARD_STRINGS.nesha,
  },
};

const isReviewCard = (card: SidebarCard): card is 'matteo' | 'aurella' | 'sayvon' | 'nesha' =>
  card === 'matteo' || card === 'aurella' || card === 'sayvon' || card === 'nesha';

export const OnboardingSidebar = ({
  currentStep,
  isShowingAccountCreationLoading,
  isShowingInviteFamily,
  sidebarCard,
  showInviteFamily,
  visibleSteps,
  currentProgressStepIndex,
  canNavigateBack,
  onProgressStepClick,
}: OnboardingSidebarProps) => {
  const shouldShrinkText = useMediaQuery('(max-height: 870px)');

  return (
    <Box sx={sideBarStyle}>
      <Box sx={logoContainerSx}>
        <Box
          component="img"
          src="../images/backrs-drop-shadow-logo-text.svg"
          alt="Backrs Logo"
          sx={backrsLogoImageSx}
        />
      </Box>
      <OnboardingSidebarItems
        currentStep={currentStep}
        isShowingAccountCreationLoading={isShowingAccountCreationLoading}
        isShowingInviteFamily={isShowingInviteFamily}
        showInviteFamily={showInviteFamily}
        visibleSteps={visibleSteps}
        currentProgressStepIndex={currentProgressStepIndex}
        canNavigateBack={canNavigateBack}
        onProgressStepClick={onProgressStepClick}
      />

      {sidebarCard && (
        <Box key={sidebarCard} sx={cardContainerSx}>
          {isReviewCard(sidebarCard) ? (
            <ReviewCard
              quote={reviewCardConfig[sidebarCard].quote}
              profileImage={reviewCardConfig[sidebarCard].profileImage}
              name={reviewCardConfig[sidebarCard].name}
              subtitle={reviewCardConfig[sidebarCard].subtitle}
              shrinkText={reviewCardConfig[sidebarCard].shrinkText && shouldShrinkText}
            />
          ) : (
            <DataLockedAndLoadedCard
              title={dataLockedConfig[sidebarCard].title}
              description={dataLockedConfig[sidebarCard].description}
              imageUrl={dataLockedConfig[sidebarCard].imageUrl}
              learnMore={{
                text: dataLockedConfig[sidebarCard].learnMore,
                onClick: () =>
                  window.open(
                    `${BACKRS_MARKETING_SITE}/${dataLockedConfig[sidebarCard].backrsPage}`,
                    '_blank',
                  ),
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const sideBarStyle: SxProps<Theme> = ({ backrs }) => ({
  width: {
    md: '50vw',
    lg: `calc(${440 / 1440} * 100%)`,
  },
  maxWidth: 440,
  height: '100dvh',
  padding: backrs.spacing['spacing-4xl'],

  display: { xs: 'none', md: 'flex' },
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',

  backgroundColor: backrs.colors['Beige/50'],
  borderRight: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const logoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-md'],
  display: 'flex',
  marginBottom: backrs.spacing['spacing-2xl'],
  minHeight: ICON_SIZES['32px'],
  alignItems: 'center',
});

const backrsLogoImageSx: SxProps<Theme> = () => ({
  height: ICON_SIZES['32px'],
});

const cardContainerSx: SxProps<Theme> = {
  marginTop: 'auto',
  width: '100%',
  animation: `${fadeIn} 400ms ease-out`,
};
